import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";

export default function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState({ state: "checking" });
  const attempts = useRef(0);
  const { refresh } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", message: "Missing session id" });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", orderId: data.order_id });
          refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired" });
          return;
        }
        if (attempts.current >= 8) {
          setStatus({ state: "pending", orderId: data.order_id });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus({ state: "error", message: "Could not verify payment" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="checkout-success-page">
        {status.state === "checking" && (
          <>
            <Clock className="w-12 h-12 mx-auto text-[#702A32] mb-4 animate-pulse" strokeWidth={1.5} />
            <h1 className="font-heading text-4xl">Confirming your payment…</h1>
            <p className="text-[#5C5A56] mt-3">This usually takes just a few seconds.</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-[#2A4B3A] mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-5xl tracking-tight">Thank you.</h1>
            <p className="text-[#5C5A56] mt-3">Your order <span className="font-mono text-[#1A1A18]">{status.orderId}</span> is confirmed. A receipt has been emailed to you.</p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/account" className="btn-primary" data-testid="success-view-orders">View my orders</Link>
              <Link to="/discover" className="btn-secondary">Keep shopping</Link>
            </div>
          </>
        )}
        {status.state === "pending" && (
          <>
            <Clock className="w-12 h-12 mx-auto text-[#C78B1B] mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-4xl">Payment is processing</h1>
            <p className="text-[#5C5A56] mt-3">We'll email you as soon as it clears.</p>
          </>
        )}
        {status.state === "expired" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-[#B73B3B] mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-4xl">This session expired</h1>
            <Link to="/cart" className="btn-primary mt-6 inline-block">Return to cart</Link>
          </>
        )}
        {status.state === "error" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-[#B73B3B] mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-4xl">Something went wrong</h1>
            <p className="text-[#5C5A56] mt-3">{status.message}</p>
          </>
        )}
      </section>
    </Layout>
  );
}
